import { Button, Modal } from '@automata/ui';
import {
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { Box, Stack } from '@mui/system';
import Graph from 'graphology';
import { willCreateCycle } from 'graphology-dag';
import { useMemo, useState } from 'react';

import { createTaskDependency } from '../../../features/graph/graphSlice';
import { selectGraphologyGraphWithTaskAndLabwareAndDataDependencies } from '../../../features/graph/selectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Task, useAllTasks, useTask } from '../../../hooks/tasks';
import { PropertiesType } from '../../../types';
import { Icon as InstrumentIcon } from '../../customIcons/instruments';
import { Icon as TaskIcon } from '../../customIcons/tasks';

export enum RelationType {
  HAPPENS_AFTER = 'HAPPENS_AFTER',
  HAPPENS_BEFORE = 'HAPPENS_BEFORE',
}

export type TaskDependencyModalProps = {
  taskId: string;
  open: boolean;
  onClose: () => void;
};

const hasCommonLabware = (taskA: Task, taskB: Task) => {
  return taskA.labware?.some((labwareA) =>
    taskB.labware?.some((labwareB) => labwareA.id === labwareB.id)
  );
};

const isLabwareLessTask = (task: Task) => !!task.isLabwareLessTask;

const hasLabware = (task: Task): boolean =>
  isLabwareLessTask(task) || !!task.labware?.length;

const areBothActionTasks = (taskA: Task, taskB: Task) =>
  taskA.propertiesType === PropertiesType.Action &&
  taskB.propertiesType === PropertiesType.Action;

const willCreateCircularDependency = (
  taskA: Task,
  taskB: Task,
  graph: Graph,
  relationType: RelationType
) =>
  relationType === RelationType.HAPPENS_BEFORE
    ? willCreateCycle(graph, taskA.id, taskB.id)
    : willCreateCycle(graph, taskB.id, taskA.id);

const canHaveDependency = (
  taskA: Task,
  taskB: Task,
  graph: Graph,
  relationType: RelationType
) => {
  // Early return if tasks are the same
  if (taskA.id === taskB.id) return false;

  // Check if both tasks are action tasks
  if (areBothActionTasks(taskA, taskB)) {
    // Check labware conditions
    const hasTaskBLabware = hasLabware(taskB);
    const hasSharedLabware = hasCommonLabware(taskA, taskB);

    if ((!hasTaskBLabware || hasSharedLabware) && !isLabwareLessTask(taskA)) {
      return false;
    }
  }

  // Final circular dependency check
  return !willCreateCircularDependency(taskA, taskB, graph, relationType);
};

export const TaskDependencyModal = ({
  taskId,
  open,
  onClose,
}: TaskDependencyModalProps) => {
  const dispatch = useAppDispatch();

  const allTasks = useAllTasks();
  const currentTask = useTask(taskId);

  const graph = useAppSelector(
    selectGraphologyGraphWithTaskAndLabwareAndDataDependencies
  );

  const [otherTask, setOtherTask] = useState<Task | null>(null);

  const [relationType, setRelationType] = useState<RelationType>(
    RelationType.HAPPENS_AFTER
  );

  const tasksForSelect = useMemo(() => {
    return Object.values(allTasks)
      .filter(
        // we populate the select with tasks that its possible for
        // the current task to have a dependency with, as well as
        // the currently selected otherTaskId - this is because
        // it is possible to have created a valid dependency
        // and then change the graph such that the dependency is no longer valid.
        // we still need the selected task for the invalid dependency to be
        // visible to the user so they can remove it, so it needs to be included
        // in the select even if it otherwise wouldn't be.
        (task) =>
          canHaveDependency(currentTask, task, graph, relationType) ||
          task.id === otherTask?.id
      )
      .sort((a, b) => a.nickname.localeCompare(b.nickname));
  }, [allTasks, currentTask, graph, otherTask?.id, relationType]);

  const handleSubmit = () => {
    if (!otherTask) return;
    dispatch(
      createTaskDependency(
        relationType === RelationType.HAPPENS_BEFORE
          ? {
              fromTaskId: currentTask.id,
              toTaskId: otherTask.id,
            }
          : {
              fromTaskId: otherTask.id,
              toTaskId: currentTask.id,
            }
      )
    );
    onClose();
  };

  const showWarnings = !hasLabware(currentTask) || tasksForSelect.length === 0;

  return (
    <Modal open={open} onClose={onClose} title="Add task dependency">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Stack gap={2} alignItems="flex-start">
          {showWarnings && (
            <Stack gap={2} mb={2} alignItems="flex-start" width="100%">
              {currentTask.propertiesType === PropertiesType.Action &&
                !hasLabware(currentTask) &&
                !isLabwareLessTask(currentTask) && (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    You cannot create a dependency for a task without labware.
                  </Alert>
                )}
              {tasksForSelect.length === 0 && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  There are no tasks this task can have a dependency with using
                  the selected relation type.
                </Alert>
              )}
            </Stack>
          )}
          <FormControl fullWidth>
            <InputLabel>Relation type</InputLabel>
            <Select<RelationType>
              label="Relation type"
              fullWidth
              value={relationType}
              onChange={(e) => setRelationType(e.target.value as RelationType)}
            >
              <MenuItem value={RelationType.HAPPENS_AFTER}>
                Happens after
              </MenuItem>
              <MenuItem value={RelationType.HAPPENS_BEFORE}>
                Happens before
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              disabled={
                tasksForSelect.length === 0 ||
                (currentTask.propertiesType === PropertiesType.Action &&
                  !hasLabware(currentTask) &&
                  !isLabwareLessTask(currentTask))
              }
              options={tasksForSelect}
              value={otherTask}
              onChange={(_, option) => {
                setOtherTask(option);
              }}
              getOptionLabel={(task) => task.nickname || ''}
              renderOption={(props, task) => (
                <Box
                  component="li"
                  {...props}
                  key={task.id + props.id} // FIXME(LT, 11/11/2024): this intermittently reports as non-unique
                  sx={{ display: 'flex', gap: 1 }}
                >
                  <TaskIcon
                    color="neutral"
                    fontSize="large"
                    iconType={task.taskType}
                  />
                  <Box sx={{ flexGrow: 2 }}>{task.nickname}</Box>
                  {task.instrument && (
                    <>
                      {task.instrument.name}
                      <InstrumentIcon
                        iconType={task.instrument.driverName}
                        fontSize="large"
                      />
                    </>
                  )}
                </Box>
              )}
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  variant="outlined"
                  label="Task"
                  placeholder="Select task"
                />
              )}
            />
          </FormControl>
          <Stack flexDirection="row" gap={1} width="100%">
            <Button
              variant="outlined"
              onClick={onClose}
              type="button"
              sx={{ flexGrow: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!otherTask}
              sx={{ flexGrow: 1 }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
};
